import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { tutorService } from '../services/tutorService';
import { buildPublicProfilePreviewFromDraft } from '../utils/tutorPreview';
import { AM_REGIONS, YEREVAN_DISTRICTS } from '../constants/armeniaGeo';
import { ARMENIA_SETTLEMENTS_BY_REGION } from '../constants/armeniaSettlementsByRegion';
import {
  saveEditProfileDraft,
  readEditProfileDraft,
  clearEditProfileDraft,
} from '../utils/editProfileDraft';
import './EditProfile.css';

const SUBJECTS = [
  'Մաթեմատիկա', 'Ֆիզիկա', 'Քիմիա', 'Կենսաբանություն',
  'Անգլերեն', 'Ռուսերեն', 'Ֆրանսերեն', 'Գերմաներեն',
  'Պատմություն', 'Աշխարհագրություն', 'Տնտեսագիտություն',
  'Հայոց լեզու', 'Հայոց պատմություն', 'Ինֆորմատիկա',
  'Ծրագրավորում', 'Գրականություն', 'Երաժշտություն', 'Շախմատ',
];

const MODE_OPTIONS = [
  { value: 'Online', label: 'Օնլայն' },
  { value: 'In-Person', label: 'Իր մոտ' },
  { value: 'Both', label: 'Ձեր մոտ' },
];

const normalizeModes = (modes) => {
  if (!Array.isArray(modes)) return [];
  const mapped = modes.map((m) => (m === 'Student-Location' ? 'Both' : m));
  return [...new Set(mapped)];
};

const parseExperienceYears = (exp) => {
  if (typeof exp === 'number' && !Number.isNaN(exp)) return exp;
  if (!exp) return 0;
  const m = String(exp).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

const EditProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tutorId, setTutorId] = useState(null);
  const [serverTutor, setServerTutor] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    region: '',
    city: '',
    district: '',
    hourlyRate: '',
    experience: '',
    description: '',
    additionalInfo: '',
    subjects: [],
    modes: [],
  });
  const [error, setError] = useState('');

  const cityOptions =
    form.region && form.region !== 'Երևան'
      ? ARMENIA_SETTLEMENTS_BY_REGION[form.region] || []
      : [];

  useEffect(() => {
    let cancelled = false;
    const restoreDraft = location.state?.restoreDraft;

    const applyDraft = (draft, serverPhotoFallback) => {
      if (!draft?.form) return false;
      const f = draft.form;
      setForm({
        name: f.name ?? '',
        phone: f.phone ?? '',
        region: f.region ?? '',
        city: f.city ?? '',
        district: f.district ?? '',
        hourlyRate: f.hourlyRate != null ? String(f.hourlyRate) : '',
        experience: f.experience != null ? String(f.experience) : '',
        description: f.description ?? '',
        additionalInfo: f.additionalInfo ?? '',
        subjects: Array.isArray(f.subjects) ? [...f.subjects] : [],
        modes: normalizeModes(f.modes),
      });
      setSavedPhoto(
        draft.savedPhoto != null ? draft.savedPhoto : (serverPhotoFallback || null)
      );
      return true;
    };

    const load = async () => {
      try {
        const t = await tutorService.fetchMyTutor();
        if (cancelled) return;

        setTutorId(t.id);
        setServerTutor(t);

        const stored = readEditProfileDraft(t.id);

        if (restoreDraft && applyDraft(restoreDraft, t.photo || null)) {
          /* restored from preview (navigation state) */
        } else if (stored && applyDraft(stored, t.photo || null)) {
          /* restored from session (e.g. browser back) */
        } else {
          setSavedPhoto(t.photo || null);
          setForm({
            name: t.name || '',
            phone: t.phone || '',
            region: t.region || '',
            city: t.city || '',
            district: t.district || '',
            hourlyRate: String(t.hourlyRate ?? ''),
            experience: String(parseExperienceYears(t.experience)),
            description: t.description || '',
            additionalInfo: t.additionalInfo || '',
            subjects: Array.isArray(t.subjects) ? [...t.subjects] : [],
            modes: normalizeModes(t.modes),
          });
        }
      } catch (err) {
        if (cancelled) return;
        if (err.message && err.message.includes('401')) {
          navigate('/login');
        } else if (err.message && err.message.includes('404')) {
          navigate('/add-tutor');
        } else {
          setError(err.message || 'Սխալ է տեղի ունեցել');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [navigate, location.key]);

  const toggleSubject = (name) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.includes(name)
        ? prev.subjects.filter((s) => s !== name)
        : [...prev.subjects, name],
    }));
  };

  const toggleMode = (value) => {
    setForm((prev) => ({
      ...prev,
      modes: prev.modes.includes(value)
        ? prev.modes.filter((m) => m !== value)
        : [...prev.modes, value],
    }));
  };

  const handleRegionChange = (e) => {
    const v = e.target.value;
    setForm((p) => ({
      ...p,
      region: v,
      ...(v === 'Երևան'
        ? { city: 'Երևան', district: '' }
        : { city: '', district: '' }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tutorId) return;
    setSaving(true);
    setError('');
    if (form.subjects.length === 0) {
      setError('Ընտրեք առնվազն մեկ ոլորտ');
      setSaving(false);
      return;
    }
    if (form.modes.length === 0) {
      setError('Ընտրեք առնվազն մեկ ձևաչափ');
      setSaving(false);
      return;
    }
    if (!form.region.trim()) {
      setError('Ընտրեք մարզը');
      setSaving(false);
      return;
    }
    if (form.region !== 'Երևան' && !form.city.trim()) {
      setError('Լրացրեք քաղաքը կամ գյուղը');
      setSaving(false);
      return;
    }
    try {
      const isYerevan = form.region === 'Երևան';
      await tutorService.updateTutor(tutorId, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        region: form.region.trim(),
        city: isYerevan ? 'Երևան' : form.city.trim(),
        district: isYerevan ? (form.district || '').trim() : '',
        hourlyRate: parseInt(form.hourlyRate, 10) || 0,
        experience: parseInt(form.experience, 10) || 0,
        description: form.description.trim(),
        additionalInfo: form.additionalInfo.trim(),
        subjects: form.subjects,
        modes: form.modes,
      });
      clearEditProfileDraft(tutorId);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Պահպանման սխալ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-profile-page">
        <div className="edit-profile-loading">Բեռնվում է...</div>
      </div>
    );
  }

  if (error && !tutorId) {
    return (
      <div className="edit-profile-page">
        <div className="edit-profile-error">{error}</div>
        <Link to="/dashboard">← Վերադառնալ</Link>
      </div>
    );
  }

  return (
    <div className="edit-profile-page">
      <div className="edit-profile-inner">
        <div className="edit-profile-head">
          <Link to="/dashboard" className="edit-profile-back">← Վերադառնալ</Link>
          <h1>Խմբագրել պրոֆիլը</h1>
          <p className="edit-profile-lead">Փոփոխությունները կպահպանվեն անմիջապես։</p>
        </div>

        <form className="edit-profile-form" onSubmit={handleSubmit}>
          {error && <div className="edit-profile-banner">{error}</div>}

          <label className="edit-field">
            <span>Անուն, ազգանուն</span>
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </label>

          <label className="edit-field">
            <span>Հեռախոս</span>
            <input
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              required
            />
          </label>

          <label className="edit-field">
            <span>Մարզ</span>
            <select
              value={form.region}
              onChange={handleRegionChange}
              required
            >
              <option value="">Ընտրել</option>
              {AM_REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>

          {form.region && form.region !== 'Երևան' && (
            <label className="edit-field">
              <span>Քաղաք / Գյուղ</span>
              <select
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                required
              >
                <option value="">Ընտրել քաղաք / գյուղ</option>
                {cityOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          )}

          {form.region === 'Երևան' && (
            <label className="edit-field">
              <span>Թաղամաս</span>
              <select
                value={form.district}
                onChange={(e) => setForm((p) => ({ ...p, district: e.target.value }))}
              >
                <option value="">Ընտրել (ըստ ցանկության)</option>
                {YEREVAN_DISTRICTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
          )}

          <div className="edit-row">
            <label className="edit-field">
              <span>Գին (դր/ժ)</span>
              <input
                type="number"
                min="0"
                value={form.hourlyRate}
                onChange={(e) => setForm((p) => ({ ...p, hourlyRate: e.target.value }))}
                required
              />
            </label>
            <label className="edit-field">
              <span>Փորձ (տարիներ)</span>
              <input
                type="number"
                min="0"
                value={form.experience}
                onChange={(e) => setForm((p) => ({ ...p, experience: e.target.value }))}
                required
              />
            </label>
          </div>

          <fieldset className="edit-fieldset">
            <legend>Ոլորտներ</legend>
            <div className="edit-chips">
              {SUBJECTS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`edit-chip ${form.subjects.includes(s) ? 'active' : ''}`}
                  onClick={() => toggleSubject(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="edit-fieldset">
            <legend>Դասի ձևաչափ</legend>
            <div className="edit-modes">
              {MODE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="edit-mode-item">
                  <input
                    type="checkbox"
                    checked={form.modes.includes(value)}
                    onChange={() => toggleMode(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="edit-field">
            <span>Նկարագրություն</span>
            <textarea
              rows={5}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              required
            />
          </label>

          <label className="edit-field">
            <span>Լրացուցիչ տեղեկություն (ըստ ցանկության)</span>
            <textarea
              rows={3}
              value={form.additionalInfo}
              onChange={(e) => setForm((p) => ({ ...p, additionalInfo: e.target.value }))}
            />
          </label>

          <div className="edit-actions">
            <button type="submit" className="edit-btn-primary" disabled={saving}>
              {saving ? 'Պահպանվում է...' : 'Պահպանել'}
            </button>
            {tutorId && serverTutor && (
              <button
                type="button"
                className="edit-btn-preview"
                onClick={() => {
                  const previewTutor = buildPublicProfilePreviewFromDraft(
                    serverTutor,
                    form,
                    savedPhoto
                  );
                  const editDraft = {
                    form: {
                      ...form,
                      subjects: [...form.subjects],
                      modes: [...form.modes],
                    },
                    savedPhoto,
                  };
                  saveEditProfileDraft(tutorId, form, savedPhoto);
                  navigate(`/tutor/${tutorId}?preview=true`, {
                    state:
                      previewTutor != null
                        ? { previewTutor, editDraft }
                        : { editDraft },
                  });
                }}
              >
                Նախադիտել
              </button>
            )}
            <Link to="/dashboard" className="edit-btn-secondary">Չեղարկել</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
